"use client";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReCaptcha from "@/utils/reCaptchaApi";
import {
  verifyForm,
  emailAFriendNotificationAPI,
} from "@/services/formSubmitAPI/client";
import { EMAIL_A_FRIEND_LABELS } from "./constants";

const captchaDisabled = process.env.NEXT_PUBLIC_RECAPTCHA_DISABLE === "0";

const EmailFriendModalForm = ({ onClose, pdpData = {}, locale = "en" }) => {
  const currentLocale =
    locale === "default" || !EMAIL_A_FRIEND_LABELS[locale] ? "en" : locale;
  const labels = EMAIL_A_FRIEND_LABELS["en"];
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    recipientsemail: false,
  });
  const [values, setValues] = useState({
    name: "",
    email: "",
    recipientsemail: "",
    message: "",
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaTouched, setCaptchaTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isValidEmail = email => EMAIL_REGEX.test(email.trim());

  const isValidRecipientEmails = value => {
    const emails = value
      .split(",")
      .map(e => e.trim())
      .filter(Boolean);
    return emails.length > 0 && emails.every(isValidEmail);
  };

  const getEmailError = () => {
    if (values.email.trim() === "") return labels.errorEmail;
    if (isValidEmail(values.email)) return "";
    return labels.errorEmailInvalid;
  };

  const getRecipientEmailError = () => {
    if (values.recipientsemail.trim() === "") return labels.errorRecipientEmail;
    if (isValidRecipientEmails(values.recipientsemail)) return "";
    return labels.errorRecipientEmailInvalid;
  };

  const errors = {
    name: values.name.trim() ? "" : labels.errorName,
    email: getEmailError(),
    recipientsemail: getRecipientEmailError(),
    captchaToken: captchaToken ? "" : labels.errorCaptcha,
  };

  const handleBlur = field => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleChange = field => e => {
    setValues(prev => ({ ...prev, [field]: e.target.value }));
  };

  // Load Google reCAPTCHA script (production only)
  // useEffect(() => {
  //   if (captchaDisabled) return;

  //   const script = document.createElement("script");
  //   script.src = "https://www.google.com/recaptcha/api.js";
  //   script.async = true;
  //   document.body.appendChild(script);

  //   return () => script.remove();
  // }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError(null);

    // Mark all fields touched to show any errors
    setTouched({ name: true, email: true, recipientsemail: true });
    setCaptchaTouched(true);

    // Stop if required fields are empty or invalid
    if (errors.name || errors.email || errors.recipientsemail) return;

    setIsSubmitting(true);

    try {
      // In dev: skip captcha, submit directly
      if (captchaDisabled) {
        const res = await emailAFriendNotificationAPI({
          ...values,
          // "g-recaptcha-response": captchaToken,
          currentURL: globalThis.location?.href || "",
          // copyRight: pdpData?.copyRight || "© 2026 Kohler Co.",
          brandName: pdpData?.brandName || "",
          description: pdpData?.description || "",
        });
        if (res) {
          setSuccess(true);
        }
        return;
      }

      // Step 1: Check captcha token exists
      if (!captchaToken) {
        setCaptchaTouched(true);
        setIsSubmitting(false);
        return;
      }

      // Step 2: Verify captcha with server
      const verifyData = await verifyForm(captchaToken);

      if (verifyData && verifyData?.data?.success) {
        // Step 3: Send form data
        const res = await emailAFriendNotificationAPI({
          ...values,
          // "g-recaptcha-response": captchaToken,
          currentURL: globalThis.location?.href || "",
          // copyRight: pdpData?.copyRight || "© 2026 Kohler Co.",
          brandName: pdpData?.brandName || "",
          description: pdpData?.description || "",
        });
        if (res) {
          setSuccess(true);
        }
      } else {
        setSubmitError(labels.errorCaptchaFailed);
      }
    } catch (err) {
      console.error(err);
      setSubmitError(labels.errorGeneric);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="w-full px-[42px] py-[32px] flex flex-col items-center justify-center text-center">
        <h2 className="w-full text-[24.5px] leading-[30px] pb-[15px] mb-[30px] font-[Helvetica-Light,_Helvetica,_sans-serif] uppercase tracking-[-0.5px] text-[#153b6b] border-b border-[#ccc] font-[200]">
          {labels.successTitle}
        </h2>
        <p className="text-[16.8px] text-[#436d07] font-[helveticaRoman,_Helvetica,_sans-serif]">
          {labels.successMessage}
        </p>
        {/* <button
          onClick={onClose}
          className="mt-4 h-[56px] w-[206px] bg-[#d8d8d8] text-[18px] font-semibold uppercase tracking-[-0.2px] text-[#0f1f35] transition hover:opacity-90"
        >
          {labels.closeButton}
        </button> */}
      </div>
    );
  }

  return (
    <div className="w-full px-[39px] pt-[50px] pb-[70px] md:pb-[50px] min-w-[1023px]:max-w-[600px]">
      {/* Heading */}
      <h2 className="text-center text-[24.5px] leading-[1.1] font-[Helvetica-Light,_Helvetica,_sans-serif] uppercase font-[200] tracking-[-0.5px] text-[#232323]">
        {labels.modalTitle}
      </h2>

      <div className="mt-[15px] border-t border-[#ccc]" />

      {/* Product Info */}
      <div className="mt-[30px] text-center">
        <p className="text-[15.4px] text-[#000]">
          {labels.sharingLabel}{" "}
          <span className="font-[helveticaBold,_Helvetica,_sans-serif] font-[400]">
            {pdpData?.brandName} {pdpData?.description}
          </span>
        </p>
      </div>

      {/* Top Right Text */}
      <div className="mt-[20px] flex justify-end gap-6 text-[14px]">
        <a
          href="https://www.corporate.kohler.com/privacy-statement"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#284c88] hover:underline"
        >
          {labels.privacyPolicy}
        </a>

        <span className="text-[#b7b7b7]">{labels.requiredFields}</span>
      </div>

      {/* Form */}
      <form className="mt-[5px]" onSubmit={handleSubmit} noValidate>
        {/* Name */}
        <div className="mb-[15px] flex items-start max-md:flex-col">
          <label
            htmlFor="name"
            className="!font-[helveticaRoman,_Helvetica,_sans-serif] !text-[14px] pr-[20px] w-[190px] pt-3 text-right text-black max-md:w-full max-md:text-left max-md:pt-0 max-md:pb-1 max-md:pr-0"
          >
            {labels.nameLabel}
          </label>

          <div className="flex-1 w-full">
            <input
              id="name"
              type="text"
              value={values.name}
              onChange={handleChange("name")}
              onBlur={() => handleBlur("name")}
              className={`p-2 h-[40px] w-full border rounded-[4px] text-[16px] outline-none transition focus:border-black ${
                touched.name && errors.name
                  ? "border-red-500"
                  : "border-[#cfcfcf]"
              }`}
            />
            {touched.name && errors.name && (
              <p role="alert" className="mt-1 text-[13px] text-red-600">
                {errors.name}
              </p>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="mb-[15px] flex items-start max-md:flex-col">
          <label
            htmlFor="email"
            className="!font-[helveticaRoman,_Helvetica,_sans-serif] !text-[14px] pr-[20px] w-[190px] pt-3 text-right text-black max-md:w-full max-md:text-left max-md:pt-0 max-md:pb-1 max-md:pr-0"
          >
            {labels.emailLabel}
          </label>

          <div className="flex-1 w-full">
            <input
              id="email"
              type="text"
              value={values.email}
              onChange={handleChange("email")}
              onBlur={() => handleBlur("email")}
              className={`p-2 h-[40px] w-full border rounded-[4px] text-[16px] outline-none transition focus:border-black ${
                touched.email && errors.email
                  ? "border-red-500"
                  : "border-[#cfcfcf]"
              }`}
            />
            {touched.email && errors.email && (
              <p role="alert" className="mt-1 text-[13px] text-red-600">
                {errors.email}
              </p>
            )}

            <p className="mt-2 text-[11px] text-[#bebebe] font-[helveticaRoman,_Helvetica,_sans-serif]">
              {labels.emailCopyHint}
            </p>
          </div>
        </div>

        {/* Recipient Email */}
        <div className="mb-[15px] flex items-start max-md:flex-col">
          <label
            htmlFor="recipientsemail"
            className="!font-[helveticaRoman,_Helvetica,_sans-serif] !text-[14px] pr-[20px] w-[190px] pt-3 text-right text-black max-md:w-full max-md:text-left max-md:pt-0 max-md:pb-1 max-md:pr-0"
          >
            {labels.recipientEmailLabel}
          </label>

          <div className="flex-1 w-full">
            <input
              id="recipientsemail"
              type="text"
              value={values.recipientsemail}
              onChange={handleChange("recipientsemail")}
              onBlur={() => handleBlur("recipientsemail")}
              className={`p-2 h-[40px] w-full border rounded-[4px] text-[16px] outline-none transition focus:border-black ${
                touched.recipientsemail && errors.recipientsemail
                  ? "border-red-500"
                  : "border-[#cfcfcf]"
              }`}
            />
            {touched.recipientsemail && errors.recipientsemail && (
              <p role="alert" className="mt-1 text-[13px] text-red-600">
                {errors.recipientsemail}
              </p>
            )}
            <p className="mt-2 text-[11px] text-[#bebebe] font-[helveticaRoman,_Helvetica,_sans-serif]">
              {labels.recipientEmailHint}
            </p>
          </div>
        </div>

        {/* Message */}
        <div className="mb-[15px] flex items-start max-md:flex-col">
          <label
            htmlFor="message"
            className="!font-[helveticaRoman,_Helvetica,_sans-serif] pr-[20px] !text-[14px] w-[190px] pt-3 text-right text-black max-md:w-full max-md:text-left max-md:pt-0 max-md:pb-1 max-md:pr-0"
          >
            {labels.messageLabel}
          </label>

          <div className="flex-1 w-full">
            <textarea
              id="message"
              rows={2}
              value={values.message}
              onChange={handleChange("message")}
              placeholder={labels.messagePlaceholder}
              className="p-3 w-full resize border border-[#ccc] rounded-[4px] py-3 text-[14px] leading-[1.428571429] outline-none transition placeholder:text-[#9e9e9e] focus:border-black font-[helveticaRoman,_Helvetica,_sans-serif] max-md:resize-none"
            />
          </div>
        </div>

        {/* reCAPTCHA */}
        {!captchaDisabled && (
          <div className="mb-[15px] flex items-start">
            <div className="w-[190px] pr-[20px] max-md:hidden" />
            <div className="flex-1 w-full">
              <div
                className={
                  captchaTouched && errors.captchaToken
                    ? "[&_iframe]:border-2 [&_iframe]:border-red-500 [&_iframe]:rounded"
                    : ""
                }
              >
                <ReCaptcha
                  siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY}
                  onChange={token => {
                    setCaptchaToken(token || "");
                    setCaptchaTouched(true);
                  }}
                  onExpired={() => setCaptchaToken("")}
                  onError={() => setCaptchaToken("")}
                />
              </div>
              {captchaTouched && errors.captchaToken && (
                <p role="alert" className="mt-1 text-[13px] text-red-600">
                  {errors.captchaToken}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Submit error */}
        {submitError && (
          <div
            role="alert"
            aria-live="assertive"
            className="mb-4 text-center text-[13px] text-red-600"
          >
            {submitError}
          </div>
        )}

        {/* Buttons */}
        <div className="flex items-center justify-center ml-[34.3333%] mb-[15px] max-md:ml-0">
          <button
            type="submit"
            disabled={isSubmitting}
            className="h-[40px] w-[50%] font-[800] bg-[#dbdbdb] text-[13px] font-[helveticaBold,_Helvetica,_sans-serif] uppercase tracking-[-0.2px] text-[#0f1f35] transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed rounded-[4px]"
          >
            {isSubmitting ? labels.sendingButton : labels.sendButton}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-[40px] w-[50%] text-[13px] font-[800] font-[helveticaBold,_Helvetica,_sans-serif] uppercase text-[#0f1f35] transition hover:opacity-70"
          >
            {labels.cancelButton}
          </button>
        </div>
      </form>
    </div>
  );
};

EmailFriendModalForm.propTypes = {
  onClose: PropTypes.func,
  pdpData: PropTypes.object,
  locale: PropTypes.string,
};

export default EmailFriendModalForm;
